import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Clock, Users, Search, Plus, Trash2, Edit2, Save, X, 
  CalendarDays, ArrowRight, Briefcase, ChevronDown, Loader2, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanTimeline = () => {
  const [viewMode, setViewMode] = useState('plan'); // 'plan' or 'manager'
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appliedNotice, setAppliedNotice] = useState(null);

  // New assignment form state
  const [newAssignment, setNewAssignment] = useState({
    planNumber: '', manager: '', startDate: '', endDate: '', notes: ''
  });

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plan-timeline/summary`);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch timeline summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchHistory = useCallback(async (planNumber, manager) => {
    try {
      const params = {};
      if (planNumber) params.plan_number = planNumber;
      if (manager) params.manager = manager;
      const res = await axios.get(`${API}/plan-timeline`, { params });
      setHistory(res.data || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    if (selectedPlan) fetchHistory(selectedPlan, null);
    else if (selectedManager) fetchHistory(null, selectedManager);
    else setHistory([]);
  }, [selectedPlan, selectedManager, fetchHistory]);

  const handleSelectPlan = (planNumber) => {
    setSelectedPlan(planNumber);
    setSelectedManager(null);
    setNewAssignment(prev => ({ ...prev, planNumber }));
  };

  const handleSelectManager = (manager) => {
    setSelectedManager(manager);
    setSelectedPlan(null);
    setNewAssignment(prev => ({ ...prev, manager }));
  };

  const handleAddAssignment = async () => {
    if (!newAssignment.planNumber || !newAssignment.manager || !newAssignment.startDate) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/plan-timeline`, newAssignment);
      const appliedAsCurrent = !!res.data?.appliedAsCurrent;
      setShowAddForm(false);
      setNewAssignment({ planNumber: selectedPlan || '', manager: selectedManager || '', startDate: '', endDate: '', notes: '' });
      if (selectedPlan) fetchHistory(selectedPlan, null);
      else if (selectedManager) fetchHistory(null, selectedManager);
      fetchSummary();
      if (appliedAsCurrent) {
        setAppliedNotice({
          plan: newAssignment.planNumber,
          manager: newAssignment.manager,
        });
        setTimeout(() => setAppliedNotice(null), 6000);
      }
    } catch (error) {
      console.error('Failed to add assignment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (planNumber, startDate) => {
    try {
      await axios.delete(`${API}/plan-timeline/${encodeURIComponent(planNumber)}/${encodeURIComponent(startDate)}`);
      if (selectedPlan) fetchHistory(selectedPlan, null);
      else if (selectedManager) fetchHistory(null, selectedManager);
      fetchSummary();
    } catch (error) {
      console.error('Failed to delete assignment:', error);
    }
  };

  const filteredPlans = summary?.plans?.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.planNumber.toLowerCase().includes(term) 
      || p.propertyName?.toLowerCase().includes(term)
      || p.currentManager?.toLowerCase().includes(term);
  }) || [];

  const filteredManagers = summary?.managers?.filter(m => {
    if (!searchTerm) return true;
    return m.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plan-timeline-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Plan Timeline</h1>
          <p className="text-sm text-[#737373] mt-1">Track and manage manager assignments across plans over time.</p>
        </div>
        <button
          data-testid="add-assignment-btn"
          onClick={() => setShowAddForm(true)}
          className="bg-[#171717] text-white rounded-[8px] px-4 py-2 text-sm font-medium hover:bg-[#262626] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          Add Assignment
        </button>
      </div>

      {/* Applied-as-current confirmation */}
      {appliedNotice && (
        <div
          data-testid="timeline-applied-banner"
          className="rounded-[10px] border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-emerald-900">
              Plan {appliedNotice.plan} → {appliedNotice.manager} · applied as current
            </p>
            <p className="text-xs text-emerald-800/80 mt-0.5">
              Open-ended assignment, so the new manager is now reflected across Plan Analysis, OC Plan Groups, Manager Profiling and Workload Decision Agent. Earlier open assignments have been closed off.
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Total Plans</p>
            <p className="text-3xl font-bold text-[#171717] mt-1">{summary.totalPlans}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Plans with History</p>
            <p className="text-3xl font-bold text-[#171717] mt-1">{summary.plansWithHistory}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Total Assignments</p>
            <p className="text-3xl font-bold text-[#171717] mt-1">{summary.totalAssignments}</p>
          </div>
        </div>
      )}

      {/* View Toggle + Search */}
      <div className="flex items-center gap-4">
        <div className="flex items-center rounded-[8px] border border-[#E5E5E5] p-1 bg-[#F8F9FA]">
          <button
            data-testid="view-by-plan-btn"
            onClick={() => { setViewMode('plan'); setSelectedManager(null); setSearchTerm(''); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors
              ${viewMode === 'plan' ? 'bg-white text-[#171717] shadow-sm border border-[#E5E5E5]' : 'text-[#737373] hover:text-[#171717]'}`}
          >
            <Briefcase className="w-3.5 h-3.5" strokeWidth={1.5} />
            By Plan
          </button>
          <button
            data-testid="view-by-manager-btn"
            onClick={() => { setViewMode('manager'); setSelectedPlan(null); setSearchTerm(''); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors
              ${viewMode === 'manager' ? 'bg-white text-[#171717] shadow-sm border border-[#E5E5E5]' : 'text-[#737373] hover:text-[#171717]'}`}
          >
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
            By Manager
          </button>
        </div>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
          <input
            data-testid="timeline-search"
            type="text"
            placeholder={viewMode === 'plan' ? 'Search plans by number, name, or manager...' : 'Search managers...'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] focus:ring-1 focus:ring-[#171717]/10 transition-colors"
          />
        </div>
      </div>

      {/* Main Content: Selection List + Timeline Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Selection List */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#F8F9FA]">
            <p className="text-xs font-semibold text-[#737373] uppercase tracking-wider">
              {viewMode === 'plan' ? `Plans (${filteredPlans.length})` : `Managers (${filteredManagers.length})`}
            </p>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-[#F5F5F5]">
            {viewMode === 'plan' ? (
              filteredPlans.slice(0, 100).map(plan => (
                <button
                  key={plan.planNumber}
                  data-testid={`select-plan-${plan.planNumber}`}
                  onClick={() => handleSelectPlan(plan.planNumber)}
                  className={`w-full px-4 py-3 text-left transition-colors
                    ${selectedPlan === plan.planNumber ? 'bg-[#F8F9FA] border-l-2 border-l-[#171717]' : 'hover:bg-[#F8F9FA]/50'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#171717] font-mono">{plan.planNumber}</p>
                      {plan.propertyName && <p className="text-[11px] text-[#737373] truncate max-w-[180px]">{plan.propertyName}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-[#525252]">{plan.currentManager}</p>
                      {plan.historyCount > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F8F9FA] border border-[#E5E5E5] text-[#737373]">
                          {plan.historyCount} entries
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              filteredManagers.map(manager => (
                <button
                  key={manager}
                  data-testid={`select-manager-${manager}`}
                  onClick={() => handleSelectManager(manager)}
                  className={`w-full px-4 py-3 text-left transition-colors
                    ${selectedManager === manager ? 'bg-[#F8F9FA] border-l-2 border-l-[#171717]' : 'hover:bg-[#F8F9FA]/50'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#F8F9FA] border border-[#E5E5E5] flex items-center justify-center">
                      <span className="text-xs font-bold text-[#525252]">{manager.charAt(0)}</span>
                    </div>
                    <p className="text-sm font-medium text-[#171717]">{manager}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: Timeline Detail */}
        <div className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          {!selectedPlan && !selectedManager ? (
            <div className="flex flex-col items-center justify-center h-[400px] text-center p-8">
              <CalendarDays className="w-12 h-12 text-[#E5E5E5] mb-4" strokeWidth={1} />
              <p className="text-sm font-medium text-[#525252]">Select a {viewMode} to view its timeline</p>
              <p className="text-xs text-[#A3A3A3] mt-1">Choose from the list on the left to see manager assignment history</p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-[#171717] font-heading">
                    {selectedPlan ? `Plan ${selectedPlan}` : selectedManager} — Assignment History
                  </h3>
                  <p className="text-xs text-[#737373]">{history.length} assignment(s) recorded</p>
                </div>
                <button
                  data-testid="add-to-timeline-btn"
                  onClick={() => {
                    setNewAssignment({
                      planNumber: selectedPlan || '',
                      manager: selectedManager || '',
                      startDate: '',
                      endDate: '',
                      notes: ''
                    });
                    setShowAddForm(true);
                  }}
                  className="bg-white border border-[#E5E5E5] text-[#171717] rounded-[8px] px-3 py-1.5 text-xs font-medium hover:bg-[#F8F9FA] transition-colors flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Add Entry
                </button>
              </div>

              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-center p-8">
                  <Clock className="w-10 h-10 text-[#E5E5E5] mb-3" strokeWidth={1} />
                  <p className="text-sm text-[#525252]">No assignment history yet</p>
                  <p className="text-xs text-[#A3A3A3] mt-1">Click "Add Entry" to record a manager assignment</p>
                </div>
              ) : (
                <div className="divide-y divide-[#F5F5F5]">
                  {history.map((entry) => (
                    <TimelineEntry
                      key={`${entry.planNumber}-${entry.startDate}`}
                      entry={entry}
                      viewMode={viewMode}
                      onDelete={() => handleDeleteAssignment(entry.planNumber, entry.startDate)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Assignment Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[10px] border border-[#E5E5E5] shadow-xl w-full max-w-md" data-testid="add-assignment-modal">
            <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#171717] font-heading">New Manager Assignment</h3>
              <button onClick={() => setShowAddForm(false)} className="p-1 rounded hover:bg-[#F8F9FA] text-[#A3A3A3]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#737373] mb-1.5">Plan Number</label>
                {selectedPlan ? (
                  <p className="text-sm font-mono font-medium text-[#171717] px-3 py-2 bg-[#F8F9FA] rounded-[8px] border border-[#E5E5E5]">{selectedPlan}</p>
                ) : (
                  <select
                    data-testid="assignment-plan-select"
                    value={newAssignment.planNumber}
                    onChange={e => setNewAssignment(prev => ({ ...prev, planNumber: e.target.value }))}
                    className="w-full px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] focus:outline-none focus:border-[#171717]"
                  >
                    <option value="">Select a plan...</option>
                    {summary?.plans?.map(p => (
                      <option key={p.planNumber} value={p.planNumber}>{p.planNumber} — {p.propertyName || 'N/A'}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#737373] mb-1.5">Manager</label>
                {selectedManager ? (
                  <p className="text-sm font-medium text-[#171717] px-3 py-2 bg-[#F8F9FA] rounded-[8px] border border-[#E5E5E5]">{selectedManager}</p>
                ) : (
                  <select
                    data-testid="assignment-manager-select"
                    value={newAssignment.manager}
                    onChange={e => setNewAssignment(prev => ({ ...prev, manager: e.target.value }))}
                    className="w-full px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] focus:outline-none focus:border-[#171717]"
                  >
                    <option value="">Select a manager...</option>
                    {summary?.managers?.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#737373] mb-1.5">Start Date</label>
                  <input
                    data-testid="assignment-start-date"
                    type="date"
                    value={newAssignment.startDate}
                    onChange={e => setNewAssignment(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] focus:outline-none focus:border-[#171717]"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#737373] mb-1.5">End Date <span className="normal-case text-[#A3A3A3]">(optional)</span></label>
                  <input
                    data-testid="assignment-end-date"
                    type="date"
                    value={newAssignment.endDate}
                    onChange={e => setNewAssignment(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] focus:outline-none focus:border-[#171717]"
                  />
                </div>
              </div>
              {!newAssignment.endDate && newAssignment.planNumber && newAssignment.manager && (
                <div className="rounded-[8px] border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    <span className="font-bold">Live handover:</span> with no end date, this becomes the current assignment for {newAssignment.planNumber}. {newAssignment.manager} will show up immediately across Plan Analysis, OC Plan Groups, Manager Profiling and the Workload Decision Agent.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#737373] mb-1.5">Notes <span className="normal-case text-[#A3A3A3]">(optional)</span></label>
                <input
                  data-testid="assignment-notes"
                  type="text"
                  value={newAssignment.notes}
                  onChange={e => setNewAssignment(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g. Manager resigned, plan reassigned"
                  className="w-full px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717]"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E5] flex justify-end gap-3 bg-[#F8F9FA] rounded-b-[10px]">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 rounded-[8px] text-sm font-medium text-[#525252] hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="save-assignment-btn"
                onClick={handleAddAssignment}
                disabled={saving || !newAssignment.planNumber || !newAssignment.manager || !newAssignment.startDate}
                className="bg-[#171717] text-white rounded-[8px] px-5 py-2 text-sm font-medium hover:bg-[#262626] transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TimelineEntry = ({ entry, viewMode, onDelete }) => {
  const formatDate = (d) => {
    if (!d) return 'Present';
    try {
      return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  return (
    <div className="px-6 py-4 flex items-start gap-4 group hover:bg-[#F8F9FA]/50 transition-colors">
      <div className="flex flex-col items-center pt-1">
        <div className={`w-3 h-3 rounded-full border-2 ${entry.endDate ? 'bg-[#E5E5E5] border-[#A3A3A3]' : 'bg-emerald-500 border-emerald-400'}`} />
        <div className="w-px h-full bg-[#E5E5E5] min-h-[20px]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {viewMode === 'manager' && (
            <span className="text-xs font-mono font-medium text-[#171717] px-2 py-0.5 rounded bg-[#F8F9FA] border border-[#E5E5E5]">{entry.planNumber}</span>
          )}
          <span className="text-sm font-medium text-[#171717]">{entry.manager}</span>
          {!entry.endDate && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#ECFDF5] text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Current
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#737373]">
          <CalendarDays className="w-3 h-3" strokeWidth={1.5} />
          <span>{formatDate(entry.startDate)}</span>
          <ArrowRight className="w-3 h-3 text-[#A3A3A3]" />
          <span>{formatDate(entry.endDate)}</span>
        </div>
        {entry.notes && <p className="text-xs text-[#A3A3A3] mt-1 italic">{entry.notes}</p>}
      </div>
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded text-[#A3A3A3] hover:text-red-500 hover:bg-red-50"
        title="Remove entry"
      >
        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default PlanTimeline;
